// useChartVoice.js — Voice orchestrator for the chart.
// Lives outside AmberScanner.jsx to keep the parser + state machine isolated.
// Reuses the existing /api/tts endpoint (MiniMax voice) for spoken replies.

import { useCallback, useEffect, useRef, useState } from "react";

// ── Symbol catalogue (short — kept here for parser speed) ──
const VOICE_SYMBOLS = [
  "XAUUSD","XAGUSD","EURUSD","GBPUSD","USDJPY","USDCHF","USDCAD","AUDUSD","NZDUSD",
  "EURJPY","GBPJPY","AUDJPY","EURGBP","EURCHF","BTCUSD","ETHUSD",
  "SPX500","US30","NDX100","UK100","GER40","JP225",
  "R_100","R_75","R_50","R_25","R_10",
  "BOOM1000","BOOM900","BOOM600","BOOM500","BOOM300",
  "CRASH1000","CRASH900","CRASH600","CRASH500","CRASH300",
  "JD100","JD75","JD50","JD25","JD10",
  "1HZ100V","1HZ90V","1HZ75V","1HZ50V","1HZ30V",
];

// ── Indicator catalogue (id → spoken aliases) ──
const VOICE_INDICATORS = [
  { id:"ema9",    al:["ema 9","nine ema"] },
  { id:"ema20",   al:["ema 20","twenty ema"] },
  { id:"ema50",   al:["ema 50","fifty ema"] },
  { id:"ema100",  al:["ema 100","hundred ema"] },
  { id:"ema200",  al:["ema 200","two hundred ema"] },
  { id:"sma20",   al:["sma 20"] },
  { id:"sma50",   al:["sma 50"] },
  { id:"sma200",  al:["sma 200"] },
  { id:"vwap",    al:["vwap"] },
  { id:"bb",      al:["bollinger","bollinger bands","bb"] },
  { id:"kc",      al:["keltner","keltner channel"] },
  { id:"donchian",al:["donchian","donchian channel"] },
  { id:"tmacg",   al:["tma cg","tma plus cg"] },
  { id:"rsi",     al:["rsi","r s i","relative strength"] },
  { id:"stoch",   al:["stochastic","stoch"] },
  { id:"cci",     al:["cci"] },
  { id:"mfi",     al:["mfi","money flow"] },
  { id:"willr",   al:["williams","williams r","williams percent r"] },
  { id:"macd",    al:["macd","m a c d"] },
  { id:"adx",     al:["adx"] },
  { id:"aroon",   al:["aroon"] },
  { id:"supertrend", al:["supertrend","super trend"] },
  { id:"psar",    al:["psar","parabolic","parabolic sar"] },
  { id:"ichimoku",al:["ichimoku","ichimoku cloud"] },
  { id:"heikin",  al:["heikin","heikin ashi"] },
  { id:"atr",     al:["atr","average true range"] },
  { id:"volume",  al:["volume","vol"] },
  { id:"obv",     al:["obv","on balance volume"] },
  { id:"cmf",     al:["cmf","chaikin money flow"] },
];

// ── Timeframe tokens — ordered by alias length DESC so "five minute" wins over "minute" ──
const VOICE_TIMEFRAMES = [
  { tf:"5m",  al:["5m","5 min","5 minute","five minute"] },
  { tf:"10m", al:["10m","10 min","10 minute","ten minute"] },
  { tf:"15m", al:["15m","15 min","15 minute","fifteen minute","quarter hour"] },
  { tf:"30m", al:["30m","30 min","30 minute","thirty minute","half hour"] },
  { tf:"1m",  al:["1m","1 min","1 minute","one minute"] },
  { tf:"2m",  al:["2m","2 min","2 minute","two minute"] },
  { tf:"3m",  al:["3m","3 min","3 minute","three minute"] },
  { tf:"4h",  al:["4h","4 hour","four hour"] },
  { tf:"1h",  al:["1h","1 hour","one hour","hourly"] },
  { tf:"2h",  al:["2h","2 hour","two hour"] },
  { tf:"1d",  al:["1d","daily","one day"] },
  { tf:"1w",  al:["1w","weekly"] },
];

const STOP_WORDS = /^(stop|go quiet|shut up|stop listening|go silent|be quiet|never mind|cancel|exit voice|release mic)$/i;

function tokenize(t) {
  return (t || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

function findFirstMatch(tokens, catalogue) {
  const joined = " " + tokens.join(" ") + " ";
  for (const item of catalogue) {
    for (const alias of item.al) {
      const needle = " " + alias.toLowerCase() + " ";
      if (joined.includes(needle)) return item;
    }
  }
  return null;
}

function findSymbol(tokens) {
  const exact = tokens.map(t => t.toUpperCase()).find(t => VOICE_SYMBOLS.includes(t));
  if (exact) return exact;
  for (let i = 0; i < tokens.length - 1; i++) {
    const a = tokens[i].toUpperCase();
    const b = tokens[i + 1].toUpperCase();
    if (/^R$/.test(a) && /^\d+$/.test(b)) return `R_${b}`;
    if (/^(BOOM|CRASH|JD|1HZ)$/.test(a) && /^\d+$/.test(b)) return `${a}${b}`;
    if (/^(SPX|US|NDX|UK|GER|JP)$/.test(a) && /^\d+$/.test(b)) return `${a}${b}`;
  }
  return null;
}

export function parseChartCommand(rawTranscript, ctx = {}) {
  const t = tokenize(rawTranscript);
  if (!t) return null;
  const tokens = t.split(" ");
  const joined = " " + t + " ";

  // Stop — checked first so "stop" always wins
  if (STOP_WORDS.test(t.trim())) {
    return { intent: "stop", payload: {}, reply: "Going quiet." };
  }

  // ── Indicators ─────────────────────────────────────────────────
  // "clear all indicators" / "wipe indicators"
  if (/\b(clear|wipe|reset)\b.*\bindicators?\b|\bremove all\b.*\bindicators?\b/.test(t)) {
    return { intent: "clear_indicators", payload: {}, reply: "Clearing all indicators." };
  }

  const removeRe = /\b(remove|delete|hide|turn off|disable)\b/;
  const addRe    = /\b(add|show|enable|turn on|apply|plot|draw)\b/;

  const ind = findFirstMatch(tokens, VOICE_INDICATORS);
  if (ind && addRe.test(t)) {
    return { intent: "add_indicator", payload: { id: ind.id }, reply: `Adding ${ind.id}.` };
  }
  if (ind && removeRe.test(t)) {
    return { intent: "remove_indicator", payload: { id: ind.id }, reply: `Removing ${ind.id}.` };
  }
  if (ind && /\bon\b|\boff\b|\bindicator\b/.test(t)) {
    const on = /\bon\b/.test(t) && !/\boff\b/.test(t);
    return {
      intent: on ? "add_indicator" : "remove_indicator",
      payload: { id: ind.id },
      reply: on ? `Adding ${ind.id}.` : `Removing ${ind.id}.`,
    };
  }

  // ── Buy / Sell — checked BEFORE symbol so "go long R_25" doesn't switch focus ──
  const buyRe  = /^(go )?(buy|long|call|enter long|take a long|take a call)\b/;
  const sellRe = /^(go )?(sell|short|put|enter short|take a short|take a put)\b/;
  if (buyRe.test(t) || (/\b(buy|long|call|enter long)\b/.test(t) && tokens.length <= 6)) {
    return { intent: "trade", payload: { direction: "CALL", symbol: ctx.symbol }, reply: `Placing CALL on ${ctx.symbol || "focused symbol"}.` };
  }
  if (sellRe.test(t) || (/\b(sell|short|put|enter short)\b/.test(t) && tokens.length <= 6)) {
    return { intent: "trade", payload: { direction: "PUT", symbol: ctx.symbol }, reply: `Placing PUT on ${ctx.symbol || "focused symbol"}.` };
  }

  // ── Timeframe — needs an intent keyword OR a direct phrase ──
  const tf = findFirstMatch(tokens, VOICE_TIMEFRAMES);
  const tfHasIntent = /\b(switch|change|set|go|use|timeframe|time frame|candle|chart)\b/.test(t);
  // Direct phrase: "five minute", "one hour", "daily", "weekly", "15m", "1 hour"
  const tfIsBare = tf && /^(\d+|one|two|three|four|five|ten|fifteen|thirty|quarter|half)\s+(minute|hour)\b|^(daily|weekly|hourly)\b|^(minute|hour)$|^\d+(m|h|d|w)$/.test(t);
  if (tf && (tfHasIntent || tfIsBare)) {
    return { intent: "set_timeframe", payload: { tf: tf.tf }, reply: `Switching to ${tf.tf}.` };
  }

  // ── Symbol lookup (hoisted — used by close + set_symbol) ──
  const sym = findSymbol(tokens);

  // ── Close — checked BEFORE set_symbol so "close R_25" doesn't switch focus ──
  if (/\b(close|exit|cancel)\b.*\b(position|trade|all|the lot)\b|\bclose all\b/.test(t)) {
    return { intent: "close_all", payload: {}, reply: "Closing all positions on focused symbol." };
  }
  if (/\bclose\b/.test(t) && sym) {
    return { intent: "close_symbol", payload: { symbol: sym }, reply: `Closing ${sym}.` };
  }

  // ── Symbol — bare reference or with intent keyword ──
  if (sym && (tokens.length <= 3 || /\b(switch|change|show|open|go|focus|to|on|load|pull up)\b/.test(t))) {
    return { intent: "set_symbol", payload: { symbol: sym }, reply: `Switching to ${sym}.` };
  }

  // ── Analyze / read chart ──
  if (/\b(analyze|analyse|analysis|what do you see|read the chart|whats happening|what is happening|what.{0,3}you.{0,3}see)\b/.test(t)) {
    return { intent: "analyze", payload: { symbol: ctx.symbol }, reply: "Running analysis." };
  }

  // ── Find trade / scan / ML ──
  if (/\b(find a trade|find trade|find setup|find entry|any setup|any setups|any trade|any entry|setup|setups|signal|entry|scan|scan for|ml|machine learning|ai predict|predict)\b/.test(t)) {
    return { intent: "find_trade", payload: { symbol: ctx.symbol }, reply: "Scanning for trade setups." };
  }

  // ── Conditions count ──
  if (/\bconditions\b/.test(t) || /\bhow many\b/.test(t)) {
    return { intent: "conditions", payload: {}, reply: "" };
  }

  // ── Zoom ──
  if (/\bzoom in\b/.test(t)) {
    return { intent: "zoom_in", payload: {}, reply: "Zooming in." };
  }
  if (/\bzoom out\b/.test(t)) {
    return { intent: "zoom_out", payload: {}, reply: "Zooming out." };
  }
  if (/\b(fit|reset|fit content|fit chart|show all)\b/.test(t) && /\b(chart|zoom|candle)\b/.test(t)) {
    return { intent: "fit_chart", payload: {}, reply: "Fitting chart." };
  }

  // ── Fullscreen ──
  if (/\bfullscreen\b/.test(t) || /\bmaximize\b/.test(t) || /\bexpand\b/.test(t)) {
    return { intent: "fullscreen", payload: {}, reply: "Toggling fullscreen." };
  }
  if (/\bexit fullscreen\b/.test(t) || /\bminimize\b/.test(t) || /\bcollapse\b/.test(t)) {
    return { intent: "fullscreen", payload: {}, reply: "Toggling fullscreen." };
  }

  // ── Strategy overlay ──
  if (/\b(strategy indicators?|strategy overlay|strategy on chart)\b/.test(t)) {
    const on = /\b(on|enable|show|turn on)\b/.test(t) && !/\b(off|disable|hide|remove)\b/.test(t);
    return {
      intent: "strategy_overlay",
      payload: { on },
      reply: on ? "Showing strategy overlay." : "Hiding strategy overlay.",
    };
  }

  // ── Colors ──
  if (/\b(colors?|color picker|customize colors?|candle colors?)\b/.test(t)) {
    return { intent: "colors", payload: {}, reply: "Opening color picker." };
  }

  // ── Indicator picker ──
  if (/\bindicators?\b/.test(t) && /\b(open|show|picker|menu)\b/.test(t)) {
    return { intent: "indicator_picker", payload: {}, reply: "Opening indicator picker." };
  }

  // ── P1 #6 LAYOUT PRESETS — save/load full chart setups ──
  // "save setup scalper-1" / "save my setup as scalper" / "remember this as scalper"
  // "load scalper-1" / "load setup scalper-1"
  const saveMatch = t.match(/\b(?:save|remember|store)(?:\s+(?:setup|my setup|this|this setup|these?\s+(?:settings?|indicators?)))?\s+(?:as\s+|to\s+|named?\s+|called\s+)?["']?([a-z0-9 _\-]{1,30})["']?\b/i);
  const loadMatch = t.match(/\b(?:load|apply|restore|open)\s+(?:setup\s+|preset\s+|the\s+setup\s+|the\s+preset\s+|my\s+setup\s+)?["']?([a-z0-9 _\-]{1,30})["']?\s*(?:setup|preset)?\b/i);
  // Disambiguate: skip if loadMatch captured something that overlaps with other intents above
  if (saveMatch && !/\b(setup|preset|my setup|this|these?)\b/i.test(t.replace(saveMatch[1], ''))) {
    // Only treat as preset save if there's no "indicators" or other intent keyword
    const name = saveMatch[1].trim().replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9\-]/g, '');
    if (name && !/^(rsi|macd|ema|sma|bollinger|atr|ichimoku|psar|supertrend|obv|vwap|tma|h[a4]?|volume|adx|cci|stoch|fib|pivot|donchian|keltner|heikin)$/.test(name)) {
      return { intent: "save_setup", payload: { name }, reply: `Saving setup as ${name}.` };
    }
  }
  if (loadMatch && /\b(?:load|apply|restore|open)\b/.test(t)) {
    const name = loadMatch[1].trim().replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9\-]/g, '');
    // Skip indicator names like "load RSI" — those are handled above
    if (name && !/^(rsi|macd|ema|sma|bollinger|atr|ichimoku|psar|supertrend|obv|vwap|tma|h[a4]?|volume|adx|cci|stoch|fib|pivot|donchian|keltner|heikin)$/.test(name)) {
      return { intent: "load_setup", payload: { name }, reply: `Loading setup ${name}.` };
    }
  }

  // ── P2 #8 SCREENSHOT VOICE ───────────────────────────────────────────
  // "screenshot this" / "take a shot" / "save this chart" / "snap the chart"
  if (/\b(?:screenshot|take\s+(?:a\s+)?(?:shot|snap|pic)|snap\s+(?:the\s+)?chart|save\s+(?:this\s+)?(?:chart|shot)|capture\s+(?:the\s+)?chart)\b/i.test(t)) {
    return { intent: "screenshot", payload: {}, reply: "Saving chart screenshot." };
  }

  // ── P1 #4 VOICE MTF ANALYSIS ─────────────────────────────────────────
  // "show me MTF" / "multi-timeframe R_25" / "timeframe analysis for XAUUSD"
  // Optional symbol; defaults to current chart symbol (resolved in dispatch)
  const mtfMatch = t.match(/\b(?:show\s+me\s+|do\s+|run\s+)?(?:mtf|multi[-\s]?timeframe|timeframe\s+analysis|multi[-\s]?tf|multi\s+tf)\b(?:\s+(?:for|on|of))?\s*([a-z0-9]+[_\s]?\d{0,4}[a-z]{0,3})?/i);
  if (mtfMatch) {
    const raw = mtfMatch[1];
    let symbol = raw ? raw.toUpperCase().replace(/^R\s+/, "R_") : null;
    return { intent: "multi_tf", payload: { symbol }, reply: symbol ? `Analyzing ${symbol} across timeframes.` : "Analyzing multi-timeframes." };
  }

  // ── P1 #5 PER-SYMBOL RISK VOICE ──────────────────────────────────────
  // "how risky is R_25?" / "risk for R_10" / "what's R_50's edge?"
  // Symbol regex: optional underscore (R_25) or space (R 25), 1HZ etc.
  const riskMatch = t.match(/(?:how\s+risky\s+is\s+|risk(?:\s+(?:score|for|of|on))?\s+|what(?:'s|\s+is)\s+(?:the\s+)?(?:risk|edge)\s+(?:for|of|on)\s+|edge\s+for\s+|edge\s+on\s+|is\s+)([a-z0-9]+[_\s]?\d{0,4}[a-z]{0,3})(?=\s|\?|$|\.|!|,|;)/i);
  if (riskMatch) {
    let symbol = riskMatch[1].toUpperCase();
    // Normalize common verbal variants: "r 25" → R_25, "vol 10" → R_10
    symbol = symbol.replace(/^R\s+/, 'R_');
    if (symbol.length >= 2 && !/^SETUP$/.test(symbol)) {
      return { intent: "risk_score", payload: { symbol }, reply: `Checking risk for ${symbol}.` };
    }
  }

  // ── P2 #12 PRICE ALERT VOICE ──────────────────────────────────────────
  // "alert me when R_25 hits 1.25" / "alert above 2750" / "alert below 4400"
  const alertMatch = t.match(/\b(?:alert|notify)\s+(?:me\s+)?(?:when\s+|if\s+|at\s+)?(?:([a-z0-9]+[_\s]?\d{0,4}[a-z]{0,3})\s+)?(?:hits?|reaches?|touches?|crosses?)\s+([0-9]+(?:\.[0-9]+)?)\b/i)
    || t.match(/\b(?:alert|set\s+alert)\s+(above|over|below|under)\s+([0-9]+(?:\.[0-9]+)?)(?:\s+(?:on|for)\s+([a-z0-9]+[_\s]?\d{0,4}[a-z]{0,3}))?/i);
  if (alertMatch) {
    let symbol = null, price = null, condition = "above";
    if (alertMatch[1] && alertMatch[2]) {
      // "alert me when R_25 hits 1.25" — symbol first, price second
      symbol = alertMatch[1].toUpperCase().replace(/^R\s+/, "R_");
      price = parseFloat(alertMatch[2]);
      // Default direction: above for round/whole numbers, below for fractions (best-effort guess)
      condition = "above";
    } else if (alertMatch[2] && (alertMatch[1] === "above" || alertMatch[1] === "over" || alertMatch[1] === "below" || alertMatch[1] === "under")) {
      // "alert above 2750" — direction first, price second, optional symbol
      price = parseFloat(alertMatch[2]);
      condition = (alertMatch[1] === "below" || alertMatch[1] === "under") ? "below" : "above";
      symbol = alertMatch[3] ? alertMatch[3].toUpperCase().replace(/^R\s+/, "R_") : null;
    }
    if (price && !isNaN(price)) {
      return { intent: "set_alert", payload: { symbol, price, condition, message: `Price ${condition} ${price}` }, reply: `Alert set: ${symbol || "current chart"} ${condition} ${price}.` };
    }
  }

  // ── P2 #13 EQUITY CURVE VOICE ────────────────────────────────────────
  // "show equity" / "show my P&L" / "hide equity"
  const equityMatch = t.match(/\b(?:show|hide|toggle)\s+(?:my\s+)?(?:equity|p\s*&\s*l|pnl|profit\s+(?:and\s+)?loss|profit\s+curve)\b/i);
  if (equityMatch) {
    const show = t.toLowerCase().includes("show");
    const hide = t.toLowerCase().includes("hide");
    return { intent: "toggle_equity", payload: { on: show || !hide }, reply: show ? "Showing equity curve." : hide ? "Hiding equity curve." : "Toggling equity curve." };
  }

  // ── P2 #11 VOICE JOURNAL ──────────────────────────────────────────────
  // "note: missed entry by 2 pips" / "journal: bullish bias on R_50" / "log this"
  // Capture everything after the keyword as the journal text
  const journalMatch = t.match(/\b(?:note|journal|log)\s*(?:this|that)?[:\s]+(.+)/i);
  if (journalMatch) {
    const text = journalMatch[1].trim();
    if (text && text.length >= 3) {
      return { intent: "journal", payload: { text }, reply: `Journaled: ${text.slice(0, 60)}${text.length > 60 ? "..." : ""}.` };
    }
  }

  return null;
}

// ── speak: MiniMax TTS via /api/tts (same path as scanner chat) ──
async function speak(text, priority = 1) {
  if (!text) return;
  try {
    let username = null;
    try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); username = u?.username || null; } catch {}
    const ttsAgent = username === "ken72" ? "kara" : "lala";
    const ttsVoice = username === "ken72" ? "English_compelling_lady1" : "English_ConfidentWoman";
    const r = await fetch("/api/tts", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
      body: JSON.stringify({ text, voice: ttsVoice, agent: ttsAgent, voice_id: ttsVoice, priority }),
    });
    if (!r.ok) return;
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    audio.onended = () => URL.revokeObjectURL(url);
    audio.onerror = () => URL.revokeObjectURL(url);
    audio.play().catch(() => {});
  } catch (e) {
    console.warn("chart-voice TTS failed:", e);
  }
}

export function useChartVoice({
  voiceRef,
  symbol,
  onSetSymbol,
  onSetTimeframe,
  onRunAnalyze,
  onRunFindTrade,
  onTrade,
  onCloseSymbol,
  onCloseAll,
  onClearIndicators,
  onSpeak,
} = {}) {
  const [voiceState, setVoiceState] = useState("idle");
  const [lastTranscript, setLastTranscript] = useState("");
  const [lastIntent, setLastIntent] = useState(null);
  const srRef = useRef(null);
  const activeRef = useRef(false);

  const stop = useCallback(() => {
    activeRef.current = false;
    try { srRef.current?.abort(); } catch {}
    srRef.current = null;
    setVoiceState("idle");
  }, []);

  const dispatch = useCallback(async (parsed, rawText) => {
    if (!parsed) {
      speak(`Sorry, I didn't catch a chart command in: ${rawText.slice(0, 60)}.`);
      return;
    }
    setLastIntent(parsed);
    const replyVia = onSpeak || speak;
    if (parsed.reply) replyVia(parsed.reply);

    try {
      switch (parsed.intent) {
        case "add_indicator":
          voiceRef?.current?.addIndicator?.(parsed.payload.id);
          break;
        case "remove_indicator":
          voiceRef?.current?.removeIndicator?.(parsed.payload.id);
          break;
        case "clear_indicators":
          voiceRef?.current?.clearIndicators?.();
          onClearIndicators?.();
          break;
        case "set_timeframe":
          onSetTimeframe?.(parsed.payload.tf);
          break;
        case "set_symbol":
          onSetSymbol?.(parsed.payload.symbol);
          break;
        case "analyze": {
          const txt = await onRunAnalyze?.(parsed.payload.symbol || symbol);
          if (txt) replyVia(txt);
          break;
        }
        case "find_trade": {
          const r = await onRunFindTrade?.(parsed.payload.symbol || symbol);
          if (r?.verdict || r?.direction) {
            const dir = (r.direction || "").toUpperCase();
            const conds = r.conditions ? `${r.conditions.met} of ${r.conditions.required} conditions met.` : "";
            replyVia(`${dir} signal on ${parsed.payload.symbol || symbol}. ${conds} ${r.confidence ? `Confidence ${r.confidence}.` : ""}`);
          } else if (r?.text) {
            replyVia(r.text);
          } else {
            replyVia("No clear setup right now.");
          }
          break;
        }
        case "trade":
          await onTrade?.(parsed.payload.symbol || symbol, parsed.payload.direction);
          break;
        case "close_symbol":
          await onCloseSymbol?.(parsed.payload.symbol);
          break;
        case "close_all":
          await onCloseAll?.();
          break;
        case "zoom_in":
          voiceRef?.current?.zoomIn?.();
          break;
        case "zoom_out":
          voiceRef?.current?.zoomOut?.();
          break;
        case "fit_chart":
          voiceRef?.current?.fitContent?.();
          break;
        case "fullscreen":
          voiceRef?.current?.toggleFullscreen?.();
          break;
        case "strategy_overlay":
          voiceRef?.current?.toggleStrategyOverlay?.(parsed.payload.on);
          break;
        case "colors":
          voiceRef?.current?.openColors?.();
          break;
        case "indicator_picker":
          voiceRef?.current?.openIndicatorPicker?.();
          break;
        // ── P1 #6 LAYOUT PRESETS dispatch ──
        case "save_setup": {
          const name = parsed.payload.name;
          const layout = voiceRef?.current?.getLayout?.();
          if (!layout) { replyVia("Could not capture chart layout."); break; }
          try {
            const key = "kara_chart_preset_" + name;
            localStorage.setItem(key, JSON.stringify(layout));
            replyVia(`Saved setup as ${name}.`);
          } catch (e) {
            replyVia(`Save failed: ${e.message}.`);
          }
          break;
        }
        case "load_setup": {
          const name = parsed.payload.name;
          try {
            const key = "kara_chart_preset_" + name;
            const raw = localStorage.getItem(key);
            if (!raw) { replyVia(`No setup named ${name} found.`); break; }
            const layout = JSON.parse(raw);
            const ok = voiceRef?.current?.applyLayout?.(layout);
            replyVia(ok ? `Loaded setup ${name}.` : `Failed to apply setup ${name}.`);
          } catch (e) {
            replyVia(`Load failed: ${e.message}.`);
          }
          break;
        }
        case "screenshot": {
          // P2 #8 — "screenshot this"
          const ok = voiceRef?.current?.screenshot?.();
          replyVia(ok === false ? "Screenshot failed." : "Screenshot saved.");
          break;
        }
        case "toggle_equity": {
          // P2 #13 — "show equity"
          const on = parsed.payload.on;
          try {
            // Try the imperative handle first; fall back to clicking the toolbar button
            if (typeof voiceRef?.current?.toggleEquity === "function") {
              voiceRef.current.toggleEquity(on);
            } else {
              document.querySelector('[data-testid="equity-button"]')?.click();
            }
          } catch (e) { /* non-fatal */ }
          break;
        }
        case "set_alert": {
          // P2 #12 — "alert me when R_25 hits 1.25"
          const p = parsed.payload;
          // Fall back to current chart symbol if not specified
          let symbol = p.symbol;
          if (!symbol) {
            try { symbol = voiceRef?.current?.getCurrentSymbol?.(); } catch {}
          }
          if (!symbol) { replyVia("Which symbol? Say 'alert above 2750 on R_25'."); break; }
          try {
            voiceRef?.current?.setPriceAlert?.({ symbol, price: p.price, condition: p.condition, message: p.message });
            replyVia(`Watching ${symbol} for ${p.condition} ${p.price.toFixed(5)}.`);
          } catch (e) {
            replyVia(`Alert failed: ${e.message}.`);
          }
          break;
        }
        case "journal": {
          // P2 #11 — "note: missed entry by 2 pips"
          const text = parsed.payload.text;
          try {
            voiceRef?.current?.addJournalEntry?.(text);
            // spoken reply is set in parser reply field already
          } catch (e) {
            replyVia(`Journal failed: ${e.message}.`);
          }
          break;
        }
        case "multi_tf": {
          // P1 #4 — "show me MTF" / "multi-timeframe R_25"
          let symbol = parsed.payload.symbol;
          if (!symbol) {
            // fall back to the currently focused chart symbol, if exposed by the imperative handle
            try {
              const cur = voiceRef?.current?.getCurrentSymbol?.();
              if (cur) symbol = cur;
            } catch {}
          }
          if (!symbol) { replyVia("Which symbol? Say 'show MTF for R_25'."); break; }
          try {
            let username = null;
            try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); username = u?.username || null; } catch {}
            const u = username || "ken72";
            const r = await fetch(`/api/analytics/multi-tf-analysis?symbol=${encodeURIComponent(symbol)}`, {
              headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
            });
            if (!r.ok) { replyVia(`MTF analysis failed for ${symbol}.`); break; }
            const d = await r.json();
            replyVia(d.voice || `No MTF data for ${symbol}.`);
            // Pass full breakdown to chart imperative for optional overlay render
            try { voiceRef?.current?.showMtf?.(d); } catch {}
          } catch (e) {
            replyVia(`MTF analysis error: ${e.message}.`);
          }
          break;
        }
        case "risk_score": {
          // "how risky is R_25?" → fetch /api/analytics/risk-scores?symbol=R_25
          const symbol = parsed.payload.symbol;
          try {
            let username = null;
            try { const u = JSON.parse(localStorage.getItem("kara_auth_user") || "null"); username = u?.username || null; } catch {}
            const u = username || "ken72";
            const r = await fetch(`/api/analytics/risk-scores?username=${u}&days=7&symbol=${encodeURIComponent(symbol)}`, {
              headers: { "Authorization": `Bearer ${localStorage.getItem("kara_auth_token") || ""}` },
            });
            if (!r.ok) { replyVia(`Risk check failed for ${symbol}.`); break; }
            const d = await r.json();
            replyVia(d.voice || `No risk data for ${symbol}.`);
            // Also expose on the imperative handle so the chart panel can highlight it
            try { voiceRef?.current?.showRiskScore?.(d.risk); } catch {}
          } catch (e) {
            replyVia(`Risk check error: ${e.message}.`);
          }
          break;
        }
        case "stop":
          stop();
          return;
        default:
          break;
      }
    } catch (e) {
      console.error("chart-voice dispatch error:", e);
      replyVia(`Error: ${e.message || "unknown"}.`);
    }
  }, [voiceRef, symbol, onSetSymbol, onSetTimeframe, onRunAnalyze, onRunFindTrade, onTrade, onCloseSymbol, onCloseAll, onClearIndicators, onSpeak, stop]);

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceState("denied");
      speak("This browser does not support voice recognition.");
      return;
    }
    try { srRef.current?.abort(); } catch {}
    activeRef.current = true;

    const sr = new SR();
    srRef.current = sr;
    sr.continuous = true;
    sr.interimResults = false;
    sr.lang = "en-US";
    sr.maxAlternatives = 3;

    sr.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (!e.results[i].isFinal) continue;
        const text = e.results[i][0].transcript.trim();
        if (!text || text.length < 2) continue;
        setLastTranscript(text);
        setVoiceState("processing");
        const parsed = parseChartCommand(text, { symbol });
        dispatch(parsed, text).finally(() => {
          if (activeRef.current) setVoiceState("recording");
        });
      }
    };

    sr.onerror = (e) => {
      if (e.error === "no-speech" || e.error === "aborted") return;
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        activeRef.current = false;
        setVoiceState("denied");
        return;
      }
      console.warn("chart-voice SR error:", e.error);
    };

    sr.onend = () => {
      if (activeRef.current && !srRef.current) return;
      if (activeRef.current) {
        try { sr.start(); } catch {}
      }
    };

    try { sr.start(); setVoiceState("recording"); }
    catch (e) {
      console.error("chart-voice SR start failed:", e);
      activeRef.current = false;
      setVoiceState("denied");
    }
  }, [symbol, dispatch]);

  const toggle = useCallback(() => {
    if (voiceState === "recording") stop();
    else start();
  }, [voiceState, start, stop]);

  useEffect(() => () => {
    activeRef.current = false;
    try { srRef.current?.abort(); } catch {}
  }, []);

  return {
    voiceState,
    lastTranscript,
    lastIntent,
    toggle,
    start,
    stop,
    parseChartCommand,
  };
}
